import { useShowToast } from "@meiden-connect/ui";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CanvasApiHttpError,
  useCanvasApi,
  type AllCanvasItem,
  type CanvasPurgeStatusResponse,
  type CanvasTrashResponse,
  type FavoriteCanvasItem,
  type RecentCanvasItem,
  type TrashedCanvasItem,
} from "../../lib/canvas-api";

export interface DashboardCanvasActionTarget {
  readonly canvasId: string;
  readonly title: string;
  readonly thumbnailKey?: string | undefined;
  readonly thumbnailUrl?: string | undefined;
  readonly updatedAt: string;
}

/** ゴミ箱のカードの操作対象（完全削除の要求へ渡す世代を持つ）。 */
export interface TrashedCanvasActionTarget extends DashboardCanvasActionTarget {
  readonly trashGeneration?: string | undefined;
}

/**
 * 完全削除の要求の結果。started は処理が始まった（済んだ）ことをサーバの応答か状態の取り直しで確かめた。
 * failed は始まっていない（カードを元に戻した）。unknown は確かめられなかった（カードは処理中のまま、一覧を取り直す）。
 */
export type PurgeRequestOutcome = "started" | "failed" | "unknown" | "busy";

type RecentData = { items: readonly RecentCanvasItem[] };
type FavoriteData = { items: readonly FavoriteCanvasItem[] };
type AllData = { items: readonly AllCanvasItem[] };
type TrashData = { items: readonly TrashedCanvasItem[] };

const QUERY_KEYS = {
  recent: ["recent-canvases"] as const,
  favorites: ["favorites"] as const,
  all: ["all-canvases"] as const,
  trash: ["trash"] as const,
};

/** 削除処理中のカードの状態を確かめ直す間隔。 */
export const PURGE_STATUS_POLL_INTERVAL_MS = 10_000;

const PURGE_STARTED_MESSAGE =
  "キャンバスを完全に削除する処理を始めました。完了するとゴミ箱から消えます。";
const PURGE_ALREADY_STARTED_MESSAGE =
  "キャンバスを完全に削除する処理は始まっています。完了するとゴミ箱から消えます。";
const PURGE_COMPLETED_MESSAGE = "キャンバスを完全に削除しました。";
const PURGE_FAILED_MESSAGE =
  "キャンバスを完全に削除できませんでした。通信状態を確認して、もう一度お試しください。";
const PURGE_UNKNOWN_MESSAGE =
  "完全に削除する処理が始まったか確認できませんでした。しばらくしてから画面を再読み込みしてください。";
const PURGE_STALE_GENERATION_MESSAGE =
  "ゴミ箱の状態を確認できないため、完全に削除できません。画面を再読み込みしてから操作してください。";
const PURGE_FORBIDDEN_MESSAGE = "このキャンバスを完全に削除する権限がありません。";
const PURGE_STATUS_MISSING_MESSAGE =
  "キャンバスが見つからないため、完全に削除できたか確認できませんでした。ゴミ箱の一覧を更新します。";

interface CacheSnapshot {
  readonly recent: RecentData | undefined;
  readonly favorites: FavoriteData | undefined;
  readonly all: AllData | undefined;
  readonly trash: TrashData | undefined;
}

export function useDashboardCanvasActions(): {
  readonly busy: boolean;
  readonly addFavorite: (target: DashboardCanvasActionTarget) => Promise<void>;
  readonly removeFavorite: (target: DashboardCanvasActionTarget) => Promise<void>;
  readonly trash: (target: DashboardCanvasActionTarget) => Promise<void>;
  readonly restore: (target: DashboardCanvasActionTarget) => Promise<void>;
  readonly purge: (
    target: TrashedCanvasActionTarget,
    confirmation: string
  ) => Promise<PurgeRequestOutcome>;
  /** そのカードの完全削除の要求が応答待ちか（状態の照会がそのカードを確かめないようにする）。 */
  readonly isPurgeRequestPending: (canvasId: string) => boolean;
} {
  const api = useCanvasApi();
  const queryClient = useQueryClient();
  const showToast = useShowToast();
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const pendingPurgeRef = useRef(new Set<string>());
  const isPurgeRequestPending = useCallback(
    (canvasId: string): boolean => pendingPurgeRef.current.has(canvasId),
    [],
  );

  const execute = useCallback(
    async (
      request: () => Promise<void>,
      optimisticUpdate: (now: string) => void,
      successMessage: string,
      onSuccess: () => Promise<void>,
      onError?: ((error: unknown) => void) | undefined,
    ): Promise<void> => {
      if (busyRef.current) return;
      busyRef.current = true;
      setBusy(true);
      let snapshot: CacheSnapshot | undefined;
      let trashIdsAfterUpdate: ReadonlySet<string> | undefined;
      try {
        snapshot = await captureSnapshot(queryClient);
        optimisticUpdate(new Date().toISOString());
        trashIdsAfterUpdate = trashIdsOf(queryClient);
        await request();
        await onSuccess();
        showToast("success", successMessage);
      } catch (error) {
        if (snapshot) restoreSnapshot(queryClient, snapshot, trashIdsAfterUpdate);
        showToast("warning", toErrorMessage(error));
        onError?.(error);
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [queryClient, showToast],
  );

  const addFavorite = useCallback(
    async (target: DashboardCanvasActionTarget): Promise<void> =>
      execute(
        () => api.addFavorite(target.canvasId),
        (now) => {
          queryClient.setQueryData<FavoriteData>(QUERY_KEYS.favorites, (current) => ({
            items: [
              {
                ...target,
                updatedAt: target.updatedAt,
                favoritedAt: now,
              },
              ...(current?.items.filter((item) => item.canvasId !== target.canvasId) ?? []),
            ],
          }));
        },
        "お気に入りに追加しました。",
        () => markAllStale(queryClient),
      ),
    [api, execute, queryClient],
  );

  const removeFavorite = useCallback(
    async (target: DashboardCanvasActionTarget): Promise<void> =>
      execute(
        () => api.removeFavorite(target.canvasId),
        () => {
          queryClient.setQueryData<FavoriteData>(QUERY_KEYS.favorites, (current) => ({
            items: current?.items.filter((item) => item.canvasId !== target.canvasId) ?? [],
          }));
        },
        "お気に入りから外しました。",
        () => markAllStale(queryClient),
      ),
    [api, execute, queryClient],
  );

  const trash = useCallback(
    async (target: DashboardCanvasActionTarget): Promise<void> => {
      let trashed: CanvasTrashResponse | undefined;
      await execute(
        async () => {
          trashed = await api.trashCanvas(target.canvasId);
        },
        (now) => {
          removeFromRegularLists(queryClient, target.canvasId);
          queryClient.setQueryData<TrashData>(QUERY_KEYS.trash, (current) => ({
            items: [
              { ...target, trashedAt: now },
              ...(current?.items.filter((item) => item.canvasId !== target.canvasId) ?? []),
            ],
          }));
        },
        "キャンバスをゴミ箱へ移動しました。",
        async () => {
          // サーバが決めた日時と世代を楽観表示のカードへ入れ、一覧を取り直さなくても、移したばかりのカードから
          // 完全削除を始められるようにする（世代を返さない旧サーバでは、一覧を取り直すまで選べない）。
          const trashGeneration = trashed?.trashGeneration;
          if (trashGeneration !== undefined) {
            updateTrashItem(queryClient, target.canvasId, {
              trashGeneration,
              ...(trashed?.trashedAt !== undefined ? { trashedAt: trashed.trashedAt } : {}),
            });
          }
          await markAllStale(queryClient);
        },
      );
    },
    [api, execute, queryClient],
  );

  const restore = useCallback(
    async (target: DashboardCanvasActionTarget): Promise<void> =>
      execute(
        () => api.restoreCanvas(target.canvasId),
        () => {
          queryClient.setQueryData<TrashData>(QUERY_KEYS.trash, (current) => ({
            items: current?.items.filter((item) => item.canvasId !== target.canvasId) ?? [],
          }));
        },
        "キャンバスを元に戻しました。",
        async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trash, refetchType: "none" }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.recent }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.favorites }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all }),
          ]);
        },
        (error) => {
          // 完全削除が始まっていた・ほかの利用者が操作した（409）、キャンバスが無い（404）。カードをサーバの一覧で
          // 取り直す（処理中なら「削除処理中」の表示になり、状態の照会が始まる）。
          if (error instanceof CanvasApiHttpError && (error.status === 404 || error.status === 409)) {
            void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trash });
          }
        },
      ),
    [api, execute, queryClient],
  );

  // 完全削除の開始。カードは先に「削除処理中」にして復元を無効にし、失敗したときは巻き戻す前に状態を
  // 取り直す（通信断・サーバエラーでも処理が始まっていれば、処理中のまま復元を戻さない）。巻き戻すのは
  // このカードだけで、応答を待つ間に状態の照会がほかのカードへ反映した結果（済んで外したカードなど）は保つ。
  const purge = useCallback(
    async (target: TrashedCanvasActionTarget, confirmation: string): Promise<PurgeRequestOutcome> => {
      if (busyRef.current) return "busy";
      const expectedTrashGeneration = target.trashGeneration;
      if (expectedTrashGeneration === undefined) {
        showToast("warning", PURGE_STALE_GENERATION_MESSAGE);
        return "failed";
      }
      busyRef.current = true;
      setBusy(true);
      // 応答を待つ間は、状態の照会がこのカードを確かめない（開始の確定より前の none で一覧を取り直し、
      // 楽観表示を消さないようにする）。
      pendingPurgeRef.current.add(target.canvasId);
      try {
        await queryClient.cancelQueries({ queryKey: QUERY_KEYS.trash });
        const original = findTrashItem(queryClient, target.canvasId);
        setTrashItemPurgeState(queryClient, target.canvasId, { purgeState: "purging" });
        try {
          const result = await api.purgeCanvas(target.canvasId, {
            confirmation,
            expectedTrashGeneration,
          });
          // 応答を待つ間に始まった一覧の取り直し（開始の確定より前の内容）を捨ててから、確定した状態を入れる。
          await queryClient.cancelQueries({ queryKey: QUERY_KEYS.trash });
          if (result.state === "purged") {
            removeTrashItem(queryClient, target.canvasId);
            showToast("success", PURGE_COMPLETED_MESSAGE);
          } else {
            setTrashItemPurgeState(queryClient, target.canvasId, { purgeState: "purging" });
            showToast("success", PURGE_STARTED_MESSAGE);
          }
          await markAllStale(queryClient);
          return "started";
        } catch (error) {
          if (error instanceof CanvasApiHttpError && error.status >= 400 && error.status < 500) {
            // 始まっていない失敗（確認文字列の不一致・権限・状態や世代の不一致・キャンバスなし）。
            revertTrashItem(queryClient, target.canvasId, original);
            showToast("warning", purgeRejectedMessage(error));
            if (error.status === 404 || error.status === 409) {
              void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trash });
            }
            return "failed";
          }
        }
        // 通信断・サーバエラー: 処理が始まったかもしれないため、巻き戻す前に状態を取り直す。
        let status: CanvasPurgeStatusResponse;
        try {
          status = await api.fetchPurgeStatus(target.canvasId);
        } catch {
          // 状態も確かめられない。処理中かもしれないため復元を戻さず、一覧をサーバの状態で取り直す。
          showToast("warning", PURGE_UNKNOWN_MESSAGE);
          void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trash });
          return "unknown";
        }
        await queryClient.cancelQueries({ queryKey: QUERY_KEYS.trash });
        if (status.state === "none") {
          revertTrashItem(queryClient, target.canvasId, original);
          showToast("warning", PURGE_FAILED_MESSAGE);
          return "failed";
        }
        if (status.state === "purged") {
          removeTrashItem(queryClient, target.canvasId);
          showToast("success", PURGE_COMPLETED_MESSAGE);
        } else {
          setTrashItemPurgeState(queryClient, target.canvasId, {
            purgeState: "purging",
            purgeRetrying: status.state === "retrying",
          });
          showToast("info", PURGE_ALREADY_STARTED_MESSAGE);
        }
        await markAllStale(queryClient);
        return "started";
      } finally {
        pendingPurgeRef.current.delete(target.canvasId);
        busyRef.current = false;
        setBusy(false);
      }
    },
    [api, queryClient, showToast],
  );

  return { busy, addFavorite, removeFavorite, trash, restore, purge, isPurgeRequestPending };
}

/**
 * 削除処理中のゴミ箱のカードの状態を、一定間隔で完全削除の状態の API から確かめ直す。済んだカードは一覧から
 * 外し、再試行を待っているカードはその表示へ替える。ゴミ箱の一覧そのもの（管理者は全件の走査）を繰り返し
 * 取得しないよう、カードごとの状態だけを読む。完全削除の要求が応答待ちのカード（isRequestPending）は
 * 確かめない（開始の確定より前の状態で楽観表示を消さないため）。
 */
export function usePurgingTrashStatusPolling(
  items: readonly TrashedCanvasItem[],
  isRequestPending?: ((canvasId: string) => boolean) | undefined,
): void {
  const api = useCanvasApi();
  const queryClient = useQueryClient();
  const showToast = useShowToast();
  const purgingKey = items
    .filter((item) => item.purgeState === "purging")
    .map((item) => item.canvasId)
    .sort()
    .join(",");

  useEffect(() => {
    if (purgingKey === "") return undefined;
    const canvasIds = purgingKey.split(",");
    let cancelled = false;
    let running = false;
    const poll = async (): Promise<void> => {
      if (cancelled || running || document.visibilityState === "hidden") return;
      running = true;
      try {
        for (const canvasId of canvasIds) {
          if (isRequestPending?.(canvasId) === true) continue;
          let status: CanvasPurgeStatusResponse | undefined;
          try {
            status = await api.fetchPurgeStatus(canvasId);
          } catch (error) {
            // 一時的な失敗は次の周期で確かめ直す。
            if (!(error instanceof CanvasApiHttpError && error.status === 404)) continue;
            if (cancelled) return;
            applyMissingCanvas(queryClient, canvasId, showToast);
            continue;
          }
          if (cancelled) return;
          applyPurgeStatus(queryClient, canvasId, status, showToast);
        }
      } finally {
        running = false;
      }
    };
    const timer = window.setInterval(() => void poll(), PURGE_STATUS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [api, isRequestPending, purgingKey, queryClient, showToast]);
}

/**
 * 状態の照会でキャンバスが見つからない（404）。完全削除は墓標を残すため、404 は削除の完了を示さない（食い違い）。
 * 完了とは知らせず、カードを外して、ゴミ箱の一覧をサーバの状態で取り直す。
 */
function applyMissingCanvas(
  queryClient: QueryClient,
  canvasId: string,
  showToast: ReturnType<typeof useShowToast>,
): void {
  const current = queryClient
    .getQueryData<TrashData>(QUERY_KEYS.trash)
    ?.items.find((item) => item.canvasId === canvasId);
  if (current === undefined) return;
  removeTrashItem(queryClient, canvasId);
  showToast("warning", PURGE_STATUS_MISSING_MESSAGE);
  void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trash });
}

function applyPurgeStatus(
  queryClient: QueryClient,
  canvasId: string,
  status: CanvasPurgeStatusResponse,
  showToast: ReturnType<typeof useShowToast>,
): void {
  const current = queryClient
    .getQueryData<TrashData>(QUERY_KEYS.trash)
    ?.items.find((item) => item.canvasId === canvasId);
  if (current === undefined) return;
  switch (status.state) {
    case "purged":
      removeTrashItem(queryClient, canvasId);
      showToast("success", PURGE_COMPLETED_MESSAGE);
      return;
    case "retrying":
    case "purging": {
      const retrying = status.state === "retrying";
      if ((current.purgeRetrying ?? false) !== retrying) {
        setTrashItemPurgeState(queryClient, canvasId, { purgeState: "purging", purgeRetrying: retrying });
      }
      return;
    }
    case "none":
      // サーバでは処理が始まっていない（楽観表示の食い違い）。一覧をサーバの状態で取り直す。
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trash });
      return;
  }
}

function setTrashItemPurgeState(
  queryClient: QueryClient,
  canvasId: string,
  state: { readonly purgeState: "purging"; readonly purgeRetrying?: boolean | undefined },
): void {
  queryClient.setQueryData<TrashData>(QUERY_KEYS.trash, (current) =>
    current === undefined
      ? current
      : {
          items: current.items.map((item) =>
            item.canvasId === canvasId
              ? { ...item, purgeState: state.purgeState, purgeRetrying: state.purgeRetrying ?? false }
              : item,
          ),
        },
  );
}

function removeTrashItem(queryClient: QueryClient, canvasId: string): void {
  queryClient.setQueryData<TrashData>(QUERY_KEYS.trash, (current) =>
    current === undefined
      ? current
      : { items: current.items.filter((item) => item.canvasId !== canvasId) },
  );
}

function findTrashItem(queryClient: QueryClient, canvasId: string): TrashedCanvasItem | undefined {
  return queryClient
    .getQueryData<TrashData>(QUERY_KEYS.trash)
    ?.items.find((item) => item.canvasId === canvasId);
}

/** 楽観表示を、このカードだけ元の内容へ戻す（ほかのカードは今の内容のまま）。 */
function revertTrashItem(
  queryClient: QueryClient,
  canvasId: string,
  original: TrashedCanvasItem | undefined,
): void {
  if (original === undefined) return;
  queryClient.setQueryData<TrashData>(QUERY_KEYS.trash, (current) =>
    current === undefined
      ? current
      : { items: current.items.map((item) => (item.canvasId === canvasId ? original : item)) },
  );
}

function updateTrashItem(
  queryClient: QueryClient,
  canvasId: string,
  fields: Partial<Pick<TrashedCanvasItem, "trashedAt" | "trashGeneration">>,
): void {
  queryClient.setQueryData<TrashData>(QUERY_KEYS.trash, (current) =>
    current === undefined
      ? current
      : {
          items: current.items.map((item) =>
            item.canvasId === canvasId ? { ...item, ...fields } : item,
          ),
        },
  );
}

function purgeRejectedMessage(error: CanvasApiHttpError): string {
  if (error.status === 403) return PURGE_FORBIDDEN_MESSAGE;
  return error.serverMessage ?? PURGE_FAILED_MESSAGE;
}

async function captureSnapshot(queryClient: QueryClient): Promise<CacheSnapshot> {
  await Promise.all(Object.values(QUERY_KEYS).map((queryKey) => queryClient.cancelQueries({ queryKey })));
  return {
    recent: queryClient.getQueryData<RecentData>(QUERY_KEYS.recent),
    favorites: queryClient.getQueryData<FavoriteData>(QUERY_KEYS.favorites),
    all: queryClient.getQueryData<AllData>(QUERY_KEYS.all),
    trash: queryClient.getQueryData<TrashData>(QUERY_KEYS.trash),
  };
}

function trashIdsOf(queryClient: QueryClient): ReadonlySet<string> {
  return new Set(
    queryClient.getQueryData<TrashData>(QUERY_KEYS.trash)?.items.map((item) => item.canvasId) ?? [],
  );
}

/**
 * 退避した 4 つの一覧へ戻す。ゴミ箱の一覧では、楽観表示の直後には一覧にあり、要求を待つ間に状態の照会が「済み」
 * として外した削除処理中のカードを戻さない（戻すと次の照会でもう一度外れ、完了の通知が二重に出る）。楽観表示で
 * 外した操作の対象のカード（復元など）は、直後の一覧に無いため必ず戻す。
 */
function restoreSnapshot(
  queryClient: QueryClient,
  snapshot: CacheSnapshot,
  trashIdsAfterUpdate: ReadonlySet<string> | undefined,
): void {
  queryClient.setQueryData(QUERY_KEYS.recent, snapshot.recent);
  queryClient.setQueryData(QUERY_KEYS.favorites, snapshot.favorites);
  queryClient.setQueryData(QUERY_KEYS.all, snapshot.all);
  const currentIds = trashIdsOf(queryClient);
  queryClient.setQueryData<TrashData>(
    QUERY_KEYS.trash,
    snapshot.trash === undefined
      ? undefined
      : {
          items: snapshot.trash.items.filter(
            (item) =>
              item.purgeState !== "purging" ||
              trashIdsAfterUpdate?.has(item.canvasId) !== true ||
              currentIds.has(item.canvasId),
          ),
        },
  );
}

function removeFromRegularLists(queryClient: QueryClient, canvasId: string): void {
  queryClient.setQueryData<RecentData>(QUERY_KEYS.recent, (current) => ({
    items: current?.items.filter((item) => item.canvasId !== canvasId) ?? [],
  }));
  queryClient.setQueryData<FavoriteData>(QUERY_KEYS.favorites, (current) => ({
    items: current?.items.filter((item) => item.canvasId !== canvasId) ?? [],
  }));
  queryClient.setQueryData<AllData>(QUERY_KEYS.all, (current) => ({
    items: current?.items.filter((item) => item.canvasId !== canvasId) ?? [],
  }));
}

async function markAllStale(queryClient: QueryClient): Promise<void> {
  await Promise.all(
    Object.values(QUERY_KEYS).map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, refetchType: "none" }),
    ),
  );
}

const ACTION_FAILED_MESSAGE =
  "キャンバスの操作に失敗しました。時間をおいてから、もう一度お試しください。";

function toErrorMessage(error: unknown): string {
  if (error instanceof CanvasApiHttpError) {
    // 4xx はサーバの文言（応答本文の { error }。利用者向けの説明）を知らせ、「API 409: {...}」のような生の本文を
    // 出さない。5xx の本文は利用者向けではない（例: internal server error）ため、汎用の文言にする。
    if (error.status < 500 && error.serverMessage !== undefined) return error.serverMessage;
    if (error.status >= 500) return ACTION_FAILED_MESSAGE;
  }
  return error instanceof Error ? error.message : "キャンバスの操作に失敗しました。";
}
